import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { realShopifyAPI, RealProduct } from '../services/realShopifyAPI';
import { MockProduct } from '../services/devShopifyAPI';

type Product = RealProduct | MockProduct;

const CATEGORY_DEFINITIONS = [
  { key: 'glossy', label: '光沢紙', keywords: ['光沢紙', '光沢', 'グロッシー'] },
  { key: 'matte', label: 'マット紙', keywords: ['マット紙', 'マット', '微粒面', 'ラスター'] },
  { key: 'washi', label: '和紙', keywords: ['和紙'] },
  { key: 'canvas', label: 'キャンバス', keywords: ['キャンバス'] },
  { key: 'others', label: 'その他', keywords: [] },
] as const;

type CategoryKey = typeof CATEGORY_DEFINITIONS[number]['key'];

const getCategoriesFromTags = (product: Product): CategoryKey[] => {
  const normalizedTags = (Array.isArray(product.tags) ? product.tags : String(product.tags).split(',')).map(tag => String(tag).trim().toLowerCase()).filter(Boolean);

  const matchedCategories = CATEGORY_DEFINITIONS.filter(category => {
    if (category.key === 'others') return false;
    return category.keywords.some(keyword =>
      normalizedTags.some(tag => tag.includes(keyword.toLowerCase()))
    );
  }).map(category => category.key as CategoryKey);

  if (matchedCategories.length > 0) {
    return matchedCategories;
  }

  return ['others'];
};

const DELIVERY_SIZES = ['A4', 'A3', 'A2', 'A1', 'B4', 'B3', 'B2', 'B1'] as const;

interface ProductSelectorProps {
  onProductSelect: (product: Product, variantId?: string) => void;
  onClose: () => void;
  filterType?: 'paper' | 'frame' | 'mat-processing' | 'panel' | 'canvas';
}

const ProductSelector: React.FC<ProductSelectorProps> = ({ onProductSelect, onClose, filterType = 'paper' }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('A4');
  const [activeCategory, setActiveCategory] = useState<CategoryKey>('glossy');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let productData: Product[];

      // 実ストアから全商品を取得
      productData = await realShopifyAPI.getAllProducts();

      // フィルタリング
      const filteredProducts = productData.filter(product => {
        if ('status' in product && product.status !== 'active') {
          return false;
        }

        const tags = Array.isArray(product.tags) ? product.tags : [product.tags].filter(Boolean);
        const title = product.title;

        if (filterType === 'paper') {
          return tags.some(tag => tag.includes('用紙'));
        } else if (filterType === 'frame') {
          const allowedKeywords = [
            '木製フレーム額入れ',
            'アルミ製フレーム額入れ',
            '木製フレーム＆ブックマット加工',
            '木製フレーム＆オーバーマット加工',
            'アルミ製フレーム＆ブックマット加工',
            'アルミ製フレーム＆オーバーマット加工'
          ];
          return allowedKeywords.some(keyword => title.includes(keyword));
        } else if (filterType === 'mat-processing') {
          const allowedKeywords = [
            'ブックマット加工',
            'オーバーマット加工'
          ];
          return allowedKeywords.some(keyword => title.includes(keyword));
        } else if (filterType === 'panel') {
          const allowedKeywords = [
            '木製パネル加工',
            'スチレンパネル加工'
          ];
          return allowedKeywords.some(keyword => title.includes(keyword));
        } else if (filterType === 'canvas') {
          const allowedKeywords = [
            'キャンバス木枠張り'
          ];
          return allowedKeywords.some(keyword => title.includes(keyword));
        }

        return true;
      });

      setProducts(filteredProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : '商品の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const groupedProducts = useMemo(() => {
    const groups = CATEGORY_DEFINITIONS.reduce<Record<CategoryKey, Product[]>>((acc, category) => {
      acc[category.key] = [];
      return acc;
    }, {} as Record<CategoryKey, Product[]>);

    products.forEach((product) => {
      const categories = getCategoriesFromTags(product);
      categories.forEach(categoryKey => {
        groups[categoryKey].push(product);
      });
    });

    return groups;
  }, [products]);

  const handleProductSelect = (product: Product) => {
    let variantId: string | undefined;

    // 用紙選択モードの場合、選択されたサイズに該当するバリエーションを探す
    if (filterType === 'paper' && selectedSize) {
      const targetVariant = product.variants?.find(v => v.title.includes(selectedSize));
      if (targetVariant) {
        variantId = String(targetVariant.id);
      }
    }

    onProductSelect(product, variantId);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 className="text-xl font-bold">商品を選択してください</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex-shrink-0">
            <div className="text-red-800">
              <strong>エラー:</strong> {error}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              商品を読み込み中...
            </div>
          ) : Object.values(groupedProducts).every(items => items.length === 0) ? (
            <div className="text-center py-8 text-gray-500">
              商品が見つかりません
            </div>
          ) : (
            <>
              {filterType === 'paper' && (
                <div className="flex-shrink-0 mb-4 border-b border-gray-200">
                  <div className="flex flex-wrap gap-2 pb-2">
                    {CATEGORY_DEFINITIONS.map(category => {
                      const hasItems = groupedProducts[category.key]?.length > 0;
                      const isActive = activeCategory === category.key;
                      return (
                        <button
                          key={category.key}
                          type="button"
                          onClick={() => setActiveCategory(category.key)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive
                            ? 'bg-blue-600 text-white shadow-md'
                            : hasItems
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                            }`}
                          disabled={!hasItems}
                        >
                          {category.label}
                          {hasItems && <span className="ml-1 text-xs opacity-80">({groupedProducts[category.key].length})</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {filterType === 'paper' && (
                <div className="flex-shrink-0 mb-4">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2">
                    <span className="text-sm font-bold text-gray-700 whitespace-nowrap">納品サイズ:</span>
                    {DELIVERY_SIZES.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${selectedSize === size
                          ? 'bg-gray-800 text-white border-gray-800'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="overflow-y-auto flex-1 pr-2">
                {(() => {
                  const items = filterType === 'paper' ? (groupedProducts[activeCategory] || []) : products;
                  if (items.length === 0) {
                    return (
                      <div className="text-center py-10 text-gray-500">
                        このカテゴリに商品はありません
                      </div>
                    )
                  }

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                      {items.map(product => {
                        let targetVariant;
                        let priceDisplay;
                        let isAvailable = true;

                        if (filterType === 'paper') {
                          targetVariant = product.variants?.find(v => v.title.includes(selectedSize));
                          if (targetVariant) {
                            priceDisplay = `¥${targetVariant.price}`;
                          } else {
                            priceDisplay = '取扱なし';
                            isAvailable = false;
                          }
                        } else {
                          targetVariant = product.variants?.[0];
                          priceDisplay = targetVariant ? `¥${targetVariant.price}〜` : 'N/A';
                        }

                        return (
                          <div
                            key={product.id}
                            className={`border border-gray-200 rounded-lg p-4 transition-colors group relative ${isAvailable
                              ? 'hover:border-blue-300 cursor-pointer bg-white'
                              : 'bg-gray-50 cursor-not-allowed opacity-60'
                              }`}
                            onClick={() => isAvailable && handleProductSelect(product)}
                          >
                            {product.status === 'draft' && (
                              <div className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded">
                                Draft
                              </div>
                            )}
                            <div className="flex space-x-4">
                              <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                                {product.images && product.images.length > 0 ? (
                                  <img
                                    src={product.images[0].src}
                                    alt={product.images[0].alt}
                                    className={`w-full h-full object-cover transition-transform duration-300 ${isAvailable ? 'group-hover:scale-105' : ''}`}
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                    No Image
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0 flex flex-col justify-center">
                                <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">
                                  {product.title}
                                </h3>
                                <div className="mt-auto flex items-baseline gap-2">
                                  <span className={`text-lg font-bold ${isAvailable ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {priceDisplay}
                                  </span>
                                  {isAvailable && filterType === 'paper' && (
                                    <span className="text-xs text-gray-500">
                                      ({selectedSize})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductSelector;
